import Bugsnag from "@bugsnag/js";

type TabState = {
  links: HTMLAnchorElement[];
  panels: HTMLElement[];
};

function loadTabImages(root: ParentNode): void {
  root.querySelectorAll<HTMLImageElement>("img[data-original]").forEach((img) => {
    const originalSrc = img.getAttribute("data-original");
    if (!originalSrc) return;
    img.src = originalSrc;
    img.removeAttribute("data-original");
  });
}

function setActiveTab(tabId: string, tabState: TabState): void {
  tabState.links.forEach((link) => {
    const parent = link.closest("li");
    if (!parent) return;
    const href = link.getAttribute("href");
    parent.classList.toggle("ui-state-active", href === `#${tabId}`);
  });

  tabState.panels.forEach((panel) => {
    const isActive = panel.id === tabId;
    panel.style.display = isActive ? "block" : "none";
    if (isActive) {
      loadTabImages(panel);
    }
  });
}

function initTabs(main: HTMLElement): void {
  const links = Array.from(
    main.querySelectorAll<HTMLAnchorElement>("#g-navi a[href^='#']"),
  );
  const panels = Array.from(main.querySelectorAll<HTMLElement>(".tab-content"));
  const tabState = { links, panels };

  if (links.length === 0 || panels.length === 0) {
    loadTabImages(main);
    return;
  }

  const panelIds = new Set(panels.map((panel) => panel.id));
  const hashTabId = window.location.hash.replace(/^#/, "");
  const firstHref = links[0].getAttribute("href");
  const firstTabId = firstHref ? firstHref.replace(/^#/, "") : "";
  const initialTabId =
    hashTabId && panelIds.has(hashTabId) ? hashTabId : firstTabId;
  if (initialTabId) {
    setActiveTab(initialTabId, tabState);
  }

  links.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const href = link.getAttribute("href");
      const tabId = href ? href.replace(/^#/, "") : "";
      if (!tabId || !panelIds.has(tabId)) return;

      setActiveTab(tabId, tabState);
      window.location.hash = `#${tabId}`;
      window.scrollTo(0, 0);
    });
  });
}

export function initCommonUi({
  bugsnag,
}: {
  bugsnag?: {
    apiKey?: string;
    appVersion?: string;
    releaseStage?: string;
  };
} = {}): void {
  if (bugsnag && bugsnag.apiKey) {
    try {
      Bugsnag.start({
        apiKey: bugsnag.apiKey,
        appVersion: bugsnag.appVersion,
        releaseStage: bugsnag.releaseStage,
        enabledReleaseStages: ["development", "production"],
      });
    } catch (error) {
      console.error("Failed to initialize Bugsnag:", error);
    }
  }

  const main = document.getElementById("main");
  if (!main) {
    loadTabImages(document);
    return;
  }
  initTabs(main);
}
